/* eslint-disable class-methods-use-this */
import Axios from 'axios';
import {
    cacheAdapterEnhancer,
    throttleAdapterEnhancer
} from 'axios-extensions';

import Page from './transitions/helpers/default-page';

// Import the transitions used by the Router in this application
import defaultTransition from './transitions/default';
import fromMenuTransition from './transitions/FromMenuTransition';

/**
 * The Router class handles the page routing by including the Routes logic from the
 * routes.js file. The Router is dependend on the page.js and axios libraries,
 * which have to be including in the project bootstrap to work.
 */
export default class Router {
    constructor(options) {
        // Configure
        this.app = options.instance || null;
        this.prefetch = options.prefetch || false;
        this.inTransition = false;
        this.transition = 'default';

        this.currentSection = options.section || 'page-home';

        // Initialize
        this.init();
    }

    init() {
        const routes = new Map();
        routes.set('/', this.startTransition.bind(this));
        routes.set('*', this.startTransition.bind(this));

        // Register page routes
        routes.forEach((routeHandler, path) => {
            page(path, routeHandler);
        });

        // Start page.js, without first dispatch
        // This without prevent page.js to use transitions on first load
        page({ dispatch: false, popstate: true });

        // window.onpopstate = () => {
        //     window.location.reload();
        // };

        // Enhance the original axios adapter with throttle and cache enhancer.
        // In axios >= 1.x, `Axios.defaults.adapter` is no longer a function
        // (it can be a string name like 'xhr' or an array of names), so we
        // resolve it to a real function via `Axios.getAdapter()` when available.
        const defaultAdapter = typeof Axios.getAdapter === 'function'
            ? Axios.getAdapter(Axios.defaults.adapter)
            : Axios.defaults.adapter;

        this.http = Axios.create({
            baseURL: '/',
            headers: {
                'Cache-Control': 'no-cache',
                'X-Requested-With': 'XMLHttpRequest'
            },
            adapter: throttleAdapterEnhancer(
                cacheAdapterEnhancer(defaultAdapter)
            )
        });

        // Listen for events to prefetch urls
        if (this.prefetch) {
            document.body.addEventListener(
                'mouseover',
                this.onLinkEnter.bind(this)
            );
            document.body.addEventListener(
                'touchstart',
                this.onLinkEnter.bind(this)
            );
        }
    }

    /**
     * Callback for the mousehover/touchstart
     *
     * @memberOf Barba.Prefetch
     * @private
     * @param  {Object} evt
     */
    onLinkEnter(evt) {
        let el = evt.target;

        // Climb up the parent tree until we find a link.
        while (el && !el.href) {
            el = el.parentNode;
        }

        // Ignore if we run out of elements or the found link has:
        // 1) A class indicating we should ignore it
        // 2) A target attribute, indicating it is not suitable for prefetching in the router context
        // 3) A `download` attribute (file download links, e.g. PDFs)
        if (!el || el.classList.contains('ignore-fetch') || el.target || el.hasAttribute('download')) {
            return;
        }

        // Only prefetch same-origin http(s) URLs that aren't pure hash/mailto/tel/js links.
        // `el.href` returns the absolute resolved URL string; we re-parse it to inspect parts.
        let parsed;
        try {
            parsed = new URL(el.href, window.location.href);
        } catch (e) {
            return;
        }

        if (parsed.origin !== window.location.origin) {
            return;
        }
        if (!/^https?:$/.test(parsed.protocol)) {
            return;
        }
        // Same page, only the hash changed — nothing to prefetch.
        if (parsed.pathname === window.location.pathname && parsed.search === window.location.search && parsed.hash) {
            return;
        }

        // Cache it. Swallow errors (404s, network issues) so prefetch failures
        // don't surface as unhandled promise rejections in the console.
        this.http.get(parsed.pathname + parsed.search, { cache: true }).catch(() => {});
    }

    /**
     * Set the application transition.
     *
     * @param {string} transition
     * @return {void}
     */
    setTransition(transition) {
        // Create a flat array from the transitions object
        const strings = _values(this.transitions);

        // Set the transition if it exists or if it's default
        if (strings.indexOf(transition) || transition === 'default') {
            this.transition = transition;
            return;
        }

        // Alert if the transition does not exist
        console.log(
            `Transition "${transition}" does not exist. Be sure to set the correct transition or use "default".`
        );
    }

    /**
     * Called when the page transition is going to start.
     *
     * @return {void}
     */
    pageTransitionStart() {
        console.log('Calling the router completion handler');
    }

    /**
     * Starts the page transition.
     *
     * @param {Object}   ctx
     * @param {Function} next
     * @return {void}
     */
    startTransition(ctx, next) {
        // Leave if we are already in transition
        if (this.inTransition) {
            return;
        }

        // Set inTransition state
        this.inTransition = true;

        EventBus.$emit('page-transition-leave');

        window.requestAnimationFrame(() => {
            this.http
                .get(ctx.path)
                .then(response => {
                    this.destroyPage();

                    switch (this.transition) {
                        case 'from-menu':
                            fromMenuTransition(response, ctx, this);
                            break;

                        default:
                            defaultTransition(response, ctx, this);
                            break;
                    }
                })
                .catch(this.onTransitionError);
        });
    }

    /**
     * Called when the page transition is completed. Normally, everything
     * should already be taken care of. However, this is the place
     * where you can do any additional page transition cleaning.
     *
     * @return {void}
     */
    pageTransitionComplete() {
        // Reset the page transition
        this.transition = 'default';

        // Make sure the new content is added, and the 'ga()' method is available.
        if (typeof ga === 'function') {
            ga('set', {
                page: window.location.pathname,
                title: document.title
            });
            ga('send', 'pageview');
        }

        // Emit completion event
        // !! Do not remove
        EventBus.$emit('page-transition-enter-complete');
        this.transition = 'default';
        this.inTransition = false;
    }

    /**
     * Raised when there was an error fetching the ajax data using axios.
     *
     * @param  {String} error
     * @return {void}
     */
    onTransitionError(error) {
        console.log(error);
    }

    /**
     * (Re)build the Vue instance by using the Page component
     *
     * @param  {DOMElement} el
     * @return {void}
     */
    buildPage(el) {
        const page = new Page({ el, parent: this.app }); // eslint-disable-line no-unused-vars

        return page;
    }

    /**
     * Destroys the current Vue instances on the page,
     * usefull before starting a transition.
     *
     * @return {void}
     */
    destroyPage() {
        // Iterate a snapshot of $children: calling `$destroy(true)` mutates
        // the parent's $children array, which would otherwise cause every
        // other sibling to be skipped (and their setInterval/listeners leak).
        const children = this.app.$children.slice();
        for (let index = 0; index < children.length; index++) {
            this.destroyChildComponent(children[index]);
        }
    }

    destroyChildComponent(component) {
        if (component === undefined) {
            return;
        }

        // Snapshot here too — same reason as destroyPage.
        const children = component.$children ? component.$children.slice() : [];
        for (let index = 0; index < children.length; index++) {
            this.destroyChildComponent(children[index]);
        }

        if (component.$options.name === 'showreel-modal') {
            return;
        }

        // Check if the child is a Vue vm instance
        if (typeof component.$destroy !== 'undefined') {
            component.$destroy(true);
        }
    }
}
